import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { SITE_DOMAIN } from "@/lib/site";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    intro: "We may collect the following types of information:",
    groups: [
      {
        label: "a. Information You Provide Directly:",
        items: [
          ["Account Information:", "When you create an account, we collect your name, email address, phone number, and password."],
          ["Profile Information:", "You may choose to provide additional information such as your profile picture, bio, interests, and preferences."],
          ["Contact Information:", "If you invite friends to join Pull Up, we may collect their contact information."],
          ["Communications:", "We collect communications between you and other users through the Services, as well as any information you provide when you contact us directly."],
        ],
      },
      {
        label: "b. Information Collected Automatically:",
        items: [
          ["Device Information:", "We may collect information about your mobile device, including its hardware model, operating system and version, unique device identifiers, and mobile network information."],
          ["Usage Data:", "We collect information about how you use our Services, including the features you use, the pages you visit, and the time you spend on the Services."],
          ["Location Information:", "We may collect precise or approximate location data from your device when the Services are running in the foreground or background."],
          ["Cookies and Similar Technologies:", "We may use cookies and similar technologies to collect information about your browsing activity."],
        ],
      },
    ],
  },
  {
    title: "2. How We Use Your Information",
    intro: "We use the information we collect for the following purposes:",
    groups: [
      {
        label: "a. To Provide and Improve Our Services:",
        items: [
          [null, "To create and manage your account."],
          [null, "To facilitate connections between users."],
          [null, "To personalize your experience on the Services."],
          [null, "To enable features such as friend groups, smart invites, plans & events, check-ins, deals & specials, and venue discovery."],
          [null, "To develop new features and improve existing ones."],
        ],
      },
      {
        label: "b. To Communicate With You:",
        items: [
          [null, "To send you important service-related notices and updates."],
          [null, "To respond to your inquiries and provide customer support."],
          [null, "To send you marketing and promotional messages, if you have opted in to receive them."],
        ],
      },
      {
        label: "c. To Ensure Safety and Security:",
        items: [
          [null, "To detect and prevent fraud, abuse, and other harmful activities."],
          [null, "To enforce our terms of service and policies."],
        ],
      },
      {
        label: "d. For Analytics and Research:",
        items: [
          [null, "To understand how our Services are used and to gather insights for research."],
        ],
      },
    ],
  },
  {
    title: "3. How We Share Your Information",
    intro: "We may share your information in the following circumstances:",
    groups: [
      {
        label: "a. With Other Users:",
        items: [
          [null, "Your profile information and location may be visible to other users depending on your privacy settings and the features you use."],
        ],
      },
      {
        label: "b. With Third-Party Service Providers:",
        items: [
          [null, "We may share your information with third-party vendors and service providers who perform services on our behalf, such as hosting, data analysis, customer support, and marketing."],
        ],
      },
      {
        label: "c. For Legal Reasons:",
        items: [
          [null, "We may disclose your information if required by law, court order, or other governmental request."],
          [null, "We may disclose your information to protect our rights, property, or safety, or the rights, property, or safety of others."],
        ],
      },
      {
        label: "d. In Connection with a Business Transfer:",
        items: [
          [null, "If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction."],
        ],
      },
    ],
  },
  {
    title: "4. Your Choices and Rights",
    intro: "You have certain choices regarding the information we collect and how we use it:",
    groups: [
      {
        items: [
          ["Account Information:", "You can update or delete your account information at any time by accessing your profile settings."],
          ["Location Information:", "You can control whether we collect your location data by adjusting your device's location settings."],
          ["Communications:", "You can opt out of receiving marketing and promotional emails by following the unsubscribe instructions in those emails."],
        ],
      },
    ],
  },
  {
    title: "5. Data Security",
    text: "We implement reasonable security measures to protect your information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the internet or electronic storage is completely secure.",
  },
  {
    title: "6. Children's Privacy",
    text: "Our Services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.",
  },
  {
    title: "7. Changes to This Privacy Policy",
    text: "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the \"Effective Date\" at the top of this Privacy Policy.",
  },
  {
    title: "8. Contact Us",
    text: "If you have any questions or concerns about this Privacy Policy or our privacy practices, please contact us at:",
    contact: true,
  },
];

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-[#0A0E0D] text-[#F4F4F0]" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#4FE3E0] transition-colors duration-300 mb-10"
          >
            <ArrowLeft className="w-4 h-4" /> Back to {SITE_DOMAIN}
          </Link>

          <h1 className="font-bold tracking-tight text-4xl sm:text-5xl text-white">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm font-medium tracking-wide text-[#4FE3E0]">
            Effective Date: December 1, 2023
          </p>
          <p className="mt-5 text-base text-zinc-400 leading-relaxed">
            Pull Up is committed to protecting your privacy. This Privacy Policy explains how we
            collect, use, disclose, and protect your information when you use our mobile
            application, website, and services (collectively, the "Services").
          </p>
        </motion.div>

        <div className="mt-12">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.title}
              data-testid={`privacy-section-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="border-t border-white/10 py-8"
            >
              <h2 className="font-bold tracking-tight text-xl sm:text-2xl text-white">{s.title}</h2>

              {s.intro && <p className="mt-3 text-sm sm:text-base text-zinc-400">{s.intro}</p>}
              {s.text && <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">{s.text}</p>}

              {s.contact && (
                <a
                  href="mailto:privacy@pullup.app"
                  data-testid="privacy-contact-email-link"
                  className="mt-3 inline-flex items-center gap-2 text-[#4FE3E0] hover:text-white transition-colors duration-300 font-medium"
                >
                  <Mail className="w-4 h-4" /> privacy@pullup.app
                </a>
              )}

              {s.groups?.map((g, gi) => (
                <div key={gi} className="mt-4">
                  {g.label && <p className="font-semibold text-white text-sm sm:text-base">{g.label}</p>}
                  <ul className="mt-2.5 space-y-2.5">
                    {g.items.map(([head, body], ii) => (
                      <li key={ii} className="flex gap-3 text-sm sm:text-base leading-relaxed">
                        <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#4FE3E0] shrink-0" />
                        <span className="text-zinc-400">
                          {head && <span className="font-semibold text-zinc-200">{head} </span>}
                          {body}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </motion.section>
          ))}
        </div>

        <div className="border-t border-white/10 pt-8 mt-2">
          <Link
            to="/"
            data-testid="privacy-home-button"
            className="inline-flex items-center gap-2 rounded-xl bg-[#4FE3E0] text-black font-semibold text-sm px-6 py-3.5 hover:bg-[#F4F4F0] transition-colors duration-300"
          >
            Back to {SITE_DOMAIN}
          </Link>
        </div>
      </div>
    </main>
  );
}
